import { PasswordInput } from "@/components/ui/PasswordInput";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Button,
  VStack,
  Input,
  Text,
  HStack,
  Badge,
} from "@chakra-ui/react";
import { UserCheck, Search } from 'lucide-react';
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Drawer, {
  DrawerHeader,
  DrawerTitle,
  DrawerBody,
  DrawerFooter,
  DrawerCloseTrigger,
} from "@/components/ui/Drawer";
import {
  FormErrorDisplay,
} from "@/components/ui/FormErrorDisplay";
import SearchableSelect, { SearchableSelectOption } from "@/components/ui/SearchableSelect";
import withHashDrawer from "@/components/ui/withHashDrawer";
import {
  CreateAssistantRequest,
  useCreateAssistantMutation,
  UserDoctorResource,
} from "../api";
import { 
  useLazyGetOrganizationsQuery, 
  useGetOrganizationDoctorsQuery 
} from "@/entities/organizations/api";
import { useFormErrorHandler } from "@/hooks/useFormErrorHandler";

// Schema definition
const assistantSchema = yup.object({
  assistant_email: yup
    .string()
    .required("Email is required")
    .email("Please enter a valid email address"),
  first_name: yup
    .string()
    .required("First name is required")
    .min(1, "First name cannot be empty")
    .max(50, "First name must be less than 50 characters"),
  last_name: yup
    .string()
    .required("Last name is required")
    .min(1, "Last name cannot be empty")
    .max(50, "Last name must be less than 50 characters"),
  password: yup
    .string()
    .optional()
    .test('min-length', 'Password must be at least 8 characters', function(value) {
      // Only validate length if password is provided
      if (!value || value.length === 0) {
        return true; // Empty is valid (optional)
      }
      return value.length >= 8;
    }),
  organization_id: yup
    .number()
    .required("Organization is required")
    .positive("Please select an organization"),
  doctor_emails: yup
    .array(yup.string().required())
    .min(1, "Please select at least one doctor")
    .default([]),
});

type AssistantFormData = yup.InferType<typeof assistantSchema>;

interface CreateNewAssistantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const CreateNewAssistantModal: React.FC<CreateNewAssistantModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | null>(null);
  const [selectedOrganization, setSelectedOrganization] = useState<SearchableSelectOption | null>(null);
  const [selectedDoctorEmails, setSelectedDoctorEmails] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  // API hooks
  const [createAssistant, { isLoading: isCreating }] = useCreateAssistantMutation();
  const [searchOrganizationsApi] = useLazyGetOrganizationsQuery();

  // Fetch doctors when organization is selected
  const {
    data: organizationDoctorsData,
    isLoading: isLoadingDoctors,
    error: doctorsError,
  } = useGetOrganizationDoctorsQuery(selectedOrganizationId as number, {
    skip: !selectedOrganizationId,
  });



  // Form setup
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(assistantSchema),
    defaultValues: {
      assistant_email: "",
      first_name: "",
      last_name: "",
      password: "",
      organization_id: undefined as number | undefined,
      doctor_emails: [] as string[],
    },
  });

  // Error handling
  const { handleSubmissionError, clearAllErrors } = useFormErrorHandler(setError, clearErrors);
  const [rootError, setRootError] = React.useState<string | null>(null);

  // Search organizations function
  const searchOrganizations = useCallback(async (query: string): Promise<SearchableSelectOption[]> => {
    try {
      const response = await searchOrganizationsApi({
        search: query.trim(),
        per_page: 50,
      }).unwrap();

      return response.data?.map(org => ({
        value: org.id.toString(),
        label: org.name,
      })) || [];
    } catch (error) {
      console.error('Error searching organizations:', error);
      return [];
    }
  }, [searchOrganizationsApi]);

  // Filter doctors based on search term
  const filteredDoctors = useMemo(() => {
    if (!organizationDoctorsData) return [];
    
    if (!searchTerm) return organizationDoctorsData;
    
    const search = searchTerm.toLowerCase();
    return organizationDoctorsData.filter(doctor => 
      doctor.name?.toLowerCase().includes(search) ||
      doctor.email?.toLowerCase().includes(search)
    );
  }, [organizationDoctorsData, searchTerm]);

  // Handle organization selection
  const handleOrganizationChange = (option: SearchableSelectOption | null) => {
    const organizationId = option ? Number(option.value) : null;
    
    // Update organization state
    setSelectedOrganizationId(organizationId);
    setSelectedOrganization(option);
    setValue('organization_id', organizationId as any);
    
    // Reset doctors and search when organization changes or is cleared
    setSelectedDoctorEmails([]);
    setValue('doctor_emails', [] as any);
    setSearchTerm(''); // Also clear the search term
    
    // Clear form errors
    clearErrors('organization_id');
    clearErrors('doctor_emails');
  };

  // Handle doctor selection
  const handleDoctorToggle = (doctor: UserDoctorResource) => {
    const newSelectedEmails = selectedDoctorEmails.includes(doctor.email)
      ? selectedDoctorEmails.filter(email => email !== doctor.email)
      : [...selectedDoctorEmails, doctor.email];
    
    setSelectedDoctorEmails(newSelectedEmails);
    setValue('doctor_emails', newSelectedEmails as any);
    clearErrors('doctor_emails');
  };

  // Submit handler
  const onSubmit = async (data: AssistantFormData) => {
    try {
      setRootError(null);

      const requestData: CreateAssistantRequest = {
        assistant_email: data.assistant_email,
        first_name: data.first_name,
        last_name: data.last_name,
        doctor_emails: selectedDoctorEmails,
        ...(data.password && { password: data.password }),
      };

      await createAssistant(requestData).unwrap();
      
      handleClose();
      onSuccess?.();
    } catch (error: any) {
      handleSubmissionError(error);
    }
  };

  // Close handler
  const handleClose = () => {
    reset();
    setSelectedOrganizationId(null);
    setSelectedOrganization(null);
    setSelectedDoctorEmails([]);
    setSearchTerm('');
    setRootError(null);
    clearAllErrors();
    onClose();
  };

  // Update form when selected doctors change
  useEffect(() => {
    setValue('doctor_emails', selectedDoctorEmails as any);
  }, [selectedDoctorEmails, setValue]);

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} size="lg">
      <DrawerHeader>
        <DrawerTitle>Create New Assistant</DrawerTitle>
        <DrawerCloseTrigger />
      </DrawerHeader>

      <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <DrawerBody>
          <VStack gap={4} align="stretch">
            {rootError && (
              <Text color="red.500" fontSize="sm">
                {rootError}
              </Text>
            )}

            {/* Assistant Email */}
            <Box>
              <Text fontWeight="medium" mb={2}>
                Assistant Email *
              </Text>
              <Controller
                name="assistant_email"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="assistant@example.com"
                    type="email"
                  />
                )}
              />
              <FormErrorDisplay error={errors.assistant_email} />
            </Box>

            {/* First Name and Last Name */}
            <HStack gap={4} align="start">
              <Box flex="1">
                <Text fontWeight="medium" mb={2}>
                  First Name *
                </Text>
                <Controller
                  name="first_name"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Enter first name"
                    />
                  )}
                />
                <FormErrorDisplay error={errors.first_name} />
              </Box>

              <Box flex="1">
                <Text fontWeight="medium" mb={2}>
                  Last Name *
                </Text>
                <Controller
                  name="last_name"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Enter last name"
                    />
                  )}
                />
                <FormErrorDisplay error={errors.last_name} />
              </Box>
            </HStack>

            {/* Password (Optional) */}
            <Box>
              <Text fontWeight="medium" mb={2}>
                Password (Optional)
              </Text>
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <PasswordInput
                    {...field}
                    type="password"
                    placeholder="Leave empty to use default password"
                  />
                )}
              />
              <FormErrorDisplay error={errors.password} />
              <Text fontSize="sm" color="gray.500" mt={1}>
                If left empty, defaults to 'password'
              </Text>
            </Box>

            {/* Organization Selection */}
            <Box>
              <Text fontWeight="medium" mb={2}>
                Organization *
              </Text>
              <SearchableSelect
                placeholder="Search and select organization..."
                onSearch={searchOrganizations}
                onChange={(value) => {
                  // Handle clear case when value is empty
                  if (!value) {
                    handleOrganizationChange(null);
                  }
                }}
                onOptionSelect={handleOrganizationChange}
                selectedOption={selectedOrganization}
                noResultsText="No organizations found"
              />
              <FormErrorDisplay error={errors.organization_id} />
            </Box>

            {/* Doctor Selection */}
            {selectedOrganizationId && (
              <Box>
                {/* Search doctors */}
                <Box>
                  <HStack mb={3}>
                    <Search size={16} />
                    <Text fontSize="sm" fontWeight="medium">Search Doctors ({filteredDoctors.length})</Text>
                  </HStack>
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by name or email..."
                  />
                </Box>

                {/* Doctor list */}
                <Box maxH="300px" overflowY="auto">
                  <VStack gap={3} align="stretch">
                    {isLoadingDoctors ? (
                      <Box 
                        p={4} 
                        textAlign="center" 
                        borderWidth={1} 
                        borderRadius="md" 
                        borderStyle="dashed"
                        borderColor="gray.300"
                        _dark={{ borderColor: "gray.600" }}
                      >
                        <Text color="gray.500" _dark={{ color: "gray.400" }}>
                          Loading doctors for {selectedOrganization?.label}...
                        </Text>
                      </Box>
                    ) : filteredDoctors.length === 0 ? (
                      <Box 
                        p={4} 
                        textAlign="center" 
                        borderWidth={1} 
                        borderRadius="md" 
                        borderStyle="dashed"
                        borderColor="gray.300"
                        _dark={{ borderColor: "gray.600" }}
                      >
                        <Text color="gray.500" _dark={{ color: "gray.400" }}>
                          {organizationDoctorsData?.length === 0 
                            ? `No doctors found in ${selectedOrganization?.label}` 
                            : "No doctors found matching your search."}
                        </Text>
                      </Box>
                    ) : (
                      filteredDoctors.map((doctor) => (
                        <HStack
                          key={doctor.id}
                          p={3}
                          borderWidth={1}
                          borderRadius="md"
                          borderColor={selectedDoctorEmails.includes(doctor.email) ? "blue.200" : "gray.200"}
                          bg={selectedDoctorEmails.includes(doctor.email) ? "blue.50" : "white"}
                          _dark={{ 
                            borderColor: selectedDoctorEmails.includes(doctor.email) ? "blue.600" : "gray.600", 
                            bg: selectedDoctorEmails.includes(doctor.email) ? "blue.900" : "gray.700" 
                          }}
                          cursor="pointer"
                          _hover={{
                            borderColor: selectedDoctorEmails.includes(doctor.email) ? "blue.300" : "gray.300",
                            _dark: {
                              borderColor: selectedDoctorEmails.includes(doctor.email) ? "blue.500" : "gray.500"
                            }
                          }}
                          justify="space-between"
                          align="center"
                          onClick={() => handleDoctorToggle(doctor)}
                        >
                          <HStack gap={3} flex={1}>
                            <Box
                              w={4}
                              h={4}
                              borderWidth={2}
                              borderRadius="sm"
                              borderColor={selectedDoctorEmails.includes(doctor.email) ? "blue.500" : "gray.300"}
                              bg={selectedDoctorEmails.includes(doctor.email) ? "blue.500" : "transparent"}
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              _dark={{
                                borderColor: selectedDoctorEmails.includes(doctor.email) ? "blue.400" : "gray.500",
                                bg: selectedDoctorEmails.includes(doctor.email) ? "blue.400" : "transparent"
                              }}
                            >
                              {selectedDoctorEmails.includes(doctor.email) && (
                                <UserCheck size={10} color="white" />
                              )}
                            </Box>
                            
                            <VStack align="start" gap={0} flex={1}>
                              <Text fontWeight="semibold" fontSize="sm">
                                {doctor.name}
                              </Text>
                              <Text fontSize="xs" color="gray.600" _dark={{ color: "gray.400" }}>
                                {doctor.email}
                              </Text>
                            </VStack>
                            
                            <Badge 
                              colorPalette={selectedDoctorEmails.includes(doctor.email) ? 'blue' : 'gray'}
                              size="sm"
                            >
                              {selectedDoctorEmails.includes(doctor.email) ? 'Selected' : 'Available'}
                            </Badge>
                          </HStack>
                        </HStack>
                      ))
                    )}
                  </VStack>
                </Box>
                <FormErrorDisplay error={errors.doctor_emails as any} />
                
                {selectedDoctorEmails.length > 0 && (
                  <Text fontSize="sm" color="gray.600" mt={2}>
                    {selectedDoctorEmails.length} doctor(s) selected
                  </Text>
                )}
              </Box>
            )}
          </VStack>
        </DrawerBody>

        <DrawerFooter>
          <HStack gap={3}>
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              colorPalette="blue"
              loading={isCreating}
            >
              {isCreating ? "Creating..." : "Create Assistant"}
            </Button>
          </HStack>
        </DrawerFooter>
      </form>
    </Drawer>
  );
};

// Hash modal version - following the same pattern as UserCreateModal
export const CreateNewAssistantHashModal = withHashDrawer(CreateNewAssistantModal, { 
  action: 'new'  // Create assistant modal: /#/new
});

export default CreateNewAssistantModal;
